import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { changeUserType } from '../../redux/auth/userSlice';
import { useTranslation } from 'react-i18next';
import '../../styles/UserDropdown.scss';
import { useLocation } from 'react-router-dom';
import { fetchAllJobs, filterJobs } from '../../redux/jobs/jobSlice';
import profileIcon from '../../assets/profileimage.jpg';
import {
  PROFILE_REMINDER_STORAGE_KEY,
  isProfileComplete
} from '../../utils/profileCompletion';

const UserDropdown = ({ logoutClick, onSellerProfileReminder }) => {
  const location = useLocation();
  const { pathname } = location;
  const isSellerPath = pathname.startsWith('/seller/') || pathname.startsWith('/calendar');
  const isBuyerPath = pathname.startsWith('/buyer/');
  const [userType, setUserType] = useState('buyer');
  const [profileImage, setProfileImage] = useState(profileIcon || '');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const _user = useSelector((state) => state.user);
  const _userProfileData = useSelector((state) => state.profile.userProfile);

  const [profileData, setProfileData] = useState({
    name: _user.displayName || t('dropdown.users-name')
  });

  useEffect(() => {
    const name =
      _userProfileData.firstname && _userProfileData.lastname
        ? `${_userProfileData.firstname} ${_userProfileData.lastname}`
        : _user.displayName || t('dropdown.users-name');
      
    const googleUrl = _user?.photoURL?.startsWith('http') ? _user.photoURL : null;
    const customUrl = _userProfileData?.url?.startsWith('http') ? _userProfileData.url : null;
    const imageUrl = googleUrl || customUrl || profileIcon;

    setProfileImage(imageUrl);
    setProfileData({ name });
  }, [_user, _userProfileData, t]);

  useEffect(() => {
    if (['buyer', 'seller'].includes(_user.userType)) {
      setUserType(_user.userType);
    }
  }, [_user.userType]);

  function switchUserType(type) {
    if (_user.userType !== type) {
      dispatch(changeUserType({ uid: _user.uid, userType: type }));
      if (
        type === 'seller' &&
        !isProfileComplete(_userProfileData, 'seller') &&
        localStorage.getItem(PROFILE_REMINDER_STORAGE_KEY) !== 'true'
      ) {
        onSellerProfileReminder?.();
      }
      const shouldGoHome =
        (_user.userType === 'buyer' && isBuyerPath) ||
        (_user.userType === 'seller' && isSellerPath) ||
        pathname.startsWith('/search');
      if (shouldGoHome) navigate('/');
    }
  }

  const seller = (
    <>
      <div className="dropdown-button-content">
        <Link to="/seller/dashboard" className="dropdown-button">{t('dropdown.Dashboard')}</Link>
      </div>
      <div className="dropdown-button-content">
        <Link to="/seller/own-jobs?tab=createJob" className="dropdown-button">{t('dropdown.Create a job')}</Link>
      </div>
      <div className="dropdown-button-content">
        <Link to="/calendar" className="dropdown-button">{t('dropdown.Calendar')}</Link>
      </div>
    </>
  );

  const buyer = (
    <>
      <div className="dropdown-button-content">
        <Link to="/buyer/orders" className="dropdown-button">{t('dropdown.Dashboard')}</Link>
      </div>
      <div className="dropdown-button-content">
        <button
          className="dropdown-button"
          onClick={async () => {
            await dispatch(fetchAllJobs());
            dispatch(filterJobs({
              title: '',
              headline: '',
              location: '',
              category: '',
              minPrice: 0,
              maxPrice: Infinity
            }));
            navigate('/search');
          }}>
          {t('dropdown.Search')}
        </button>
      </div>
    </>
  );

  return (
    <div className="user-dropdown-container">
      <div className="dropdown-button-container">
        <div className="user-info-image">
          <img className="rounded-image" src={profileImage}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = profileIcon;
            }}
            referrerPolicy="no-referrer"
            alt="User Avatar" />
        </div>
        <div className="dropdown-button-content">
          <p style={{ fontSize: 18, fontWeight: 600 }}>{profileData.name}</p>
        </div>
        {userType === 'seller' ? seller : buyer}
      </div>
      <div className="switch-user-content">
        <div className="switch-user-label">
          <p>{t('dropdown.Switch User')}</p>
        </div>
        <div className="switch-user-buttons">
          <button
            className={`switch-button ${userType === 'buyer' && 'selected'}`}
            onClick={() => switchUserType('buyer')}>
            {t('dropdown.Buyer')}
          </button>
          <button
            className={`switch-button ${userType === 'seller' && 'selected'}`}
            onClick={() => switchUserType('seller')}>
            {t('dropdown.Seller')}
          </button>
        </div>
      </div>
      <div className="logout-button-content">
        <button
          className="logout-button"
          onClick={() => {
            logoutClick();
          }}>
          {t('dropdown.Log out')}
        </button>
      </div>
    </div>
  );
};

export default UserDropdown;
